/**
 * Package Builder - Custom Element
 * Handles the interactive package builder drawer for tractor product cards.
 */

class PackageBuilder extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 0;
    this.mode = 'package'; // 'package' or 'tractor-only'
    this.selectedAttachments = new Set();
    this.selectedTrailer = null;
    this.tractorPrice = 0;
    this.tractorTitle = '';
    this.tractorVariantId = null;
    this.savingsPercent = 0;
    this.monthlyRate = 72; // months for financing
  }

  connectedCallback() {
    this.overlay = this.previousElementSibling;
    this.tractorPrice = parseFloat(this.dataset.tractorPrice) || 0;
    this.tractorTitle = this.dataset.tractorTitle || '';
    this.tractorVariantId = this.dataset.tractorVariantId || '';
    this.savingsPercent = parseFloat(this.dataset.savingsPercent) || 10;
    this.monthlyRate = parseInt(this.dataset.monthlyRate) || 72;

    this.bindEvents();
    this.updateSummary();
    this.updatePricing();
    this.setStep(0);
  }

  bindEvents() {
    // Close button
    const closeBtn = this.querySelector('[data-builder-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Mode toggle
    const toggleBtns = this.querySelectorAll('[data-builder-toggle]');
    toggleBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.builderToggle;
        this.setMode(mode);
      });
    });

    // Step navigation
    const stepBtns = this.querySelectorAll('[data-builder-step]');
    stepBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const step = parseInt(btn.dataset.builderStep);
        this.setStep(step);
      });
    });

    // Attachment items (checkbox behavior)
    const attachmentItems = this.querySelectorAll('[data-attachment-id]');
    attachmentItems.forEach(item => {
      item.addEventListener('click', () => {
        const id = item.dataset.attachmentId;
        const price = parseFloat(item.dataset.attachmentPrice) || 0;
        const title = item.dataset.attachmentTitle || '';

        if (this.selectedAttachments.has(id)) {
          this.selectedAttachments.delete(id);
          item.classList.remove('is-selected');
        } else {
          this.selectedAttachments.add(id);
          item.classList.add('is-selected');
        }

        this.updateSummary();
        this.updatePricing();
      });
    });

    // Trailer items (radio behavior)
    const trailerItems = this.querySelectorAll('[data-trailer-id]');
    trailerItems.forEach(item => {
      item.addEventListener('click', () => {
        const id = item.dataset.trailerId;

        // Deselect all trailers
        trailerItems.forEach(t => t.classList.remove('is-selected'));

        if (this.selectedTrailer === id) {
          this.selectedTrailer = null;
        } else {
          this.selectedTrailer = id;
          item.classList.add('is-selected');
        }

        this.updateSummary();
        this.updatePricing();
      });
    });

    // Continue Package button
    const continueBtn = this.querySelector('[data-builder-continue]');
    if (continueBtn) {
      continueBtn.addEventListener('click', () => this.addPackageToCart());
    }

    // Continue as Tractor Only button
    const tractorOnlyBtn = this.querySelector('[data-builder-tractor-only]');
    if (tractorOnlyBtn) {
      tractorOnlyBtn.addEventListener('click', () => this.addTractorToCart());
    }



    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  open() {
    this.classList.add('is-open');
    if (this.overlay) this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.classList.remove('is-open');
    if (this.overlay) this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  setMode(mode) {
    this.mode = mode;
    this.dataset.mode = mode;

    const toggleBtns = this.querySelectorAll('[data-builder-toggle]');
    toggleBtns.forEach(btn => {
      btn.classList.toggle('is-active', btn.dataset.builderToggle === mode);
    });

    // Toggle CTA buttons
    const continueBtn = this.querySelector('[data-builder-continue]');
    const tractorBtn = this.querySelector('[data-builder-tractor-only]');
    if (continueBtn) continueBtn.style.display = mode === 'package' ? '' : 'none';
    if (tractorBtn) tractorBtn.style.display = mode === 'tractor-only' ? '' : 'none';

    this.updatePricing();
  }

  setStep(step) {
    this.currentStep = step;

    // Update step indicators
    const steps = this.querySelectorAll('[data-builder-step]');
    steps.forEach((s, i) => {
      s.classList.toggle('is-active', i === step);
      s.classList.toggle('is-completed', i < step);
    });

    // Update connectors
    const connectors = this.querySelectorAll('.package-builder__step-connector');
    connectors.forEach((c, i) => {
      c.classList.toggle('is-completed', i < step);
    });

    // Update panels
    const panels = this.querySelectorAll('[data-builder-panel]');
    panels.forEach((p, i) => {
      p.classList.toggle('is-active', i === step);
    });
  }

  getSelectedItems() {
    const items = [];

    // Tractor is always included
    items.push({
      title: this.tractorTitle,
      price: this.tractorPrice,
      variantId: this.tractorVariantId
    });

    // Attachments
    this.querySelectorAll('[data-attachment-id].is-selected').forEach(item => {
      items.push({
        title: item.dataset.attachmentTitle,
        price: parseFloat(item.dataset.attachmentPrice) || 0,
        variantId: item.dataset.attachmentVariantId || ''
      });
    });

    // Trailer
    if (this.selectedTrailer) {
      const trailerEl = this.querySelector(`[data-trailer-id="${this.selectedTrailer}"]`);
      if (trailerEl) {
        items.push({
          title: trailerEl.dataset.trailerTitle,
          price: parseFloat(trailerEl.dataset.trailerPrice) || 0,
          variantId: trailerEl.dataset.trailerVariantId || ''
        });
      }
    }

    return items;
  }

  updateSummary() {
    const summaryList = this.querySelector('[data-builder-summary-list]');
    if (!summaryList) return;

    const items = this.getSelectedItems();

    summaryList.innerHTML = items.map(item => `
      <div class="package-builder__summary-item">
        <span class="package-builder__summary-item-icon">
          <svg viewBox="0 0 12 12"><polyline points="2 6 5 9 10 3"></polyline></svg>
        </span>
        ${this.escapeHtml(item.title)}
      </div>
    `).join('');
  }

  updatePricing() {
    const totalEl = this.querySelector('[data-builder-total]');
    const savingsEl = this.querySelector('[data-builder-savings]');
    const monthlyEl = this.querySelector('[data-builder-monthly]');

    if (!totalEl) return;

    let total = 0;

    if (this.mode === 'tractor-only') {
      total = this.tractorPrice;
    } else {
      const items = this.getSelectedItems();
      const rawTotal = items.reduce((sum, item) => sum + item.price, 0);
      const savings = rawTotal * (this.savingsPercent / 100);
      total = rawTotal - savings;

      if (savingsEl) {
        savingsEl.textContent = `Savings: ${this.formatCurrency(savings)}`;
        savingsEl.style.display = savings > 0 ? 'inline-block' : 'none';
      }
    }

    totalEl.textContent = this.formatCurrency(total);

    if (monthlyEl) {
      const monthly = total / this.monthlyRate;
      monthlyEl.innerHTML = `Est. Monthly Payment: <strong>${this.formatCurrency(monthly)}/mo*</strong>`;
    }
  }

  formatCurrency(amount) {
    return '$' + amount.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  async addPackageToCart() {
    const items = this.getSelectedItems()
      .filter(item => item.variantId)
      .map(item => ({
        id: parseInt(item.variantId),
        quantity: 1
      }));

    if (items.length === 0) return;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        this.close();
        window.location.href = '/cart';
      }
    } catch (error) {
      console.error('Failed to add package to cart:', error);
    }
  }

  async addTractorToCart() {
    if (!this.tractorVariantId) return;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(this.tractorVariantId), quantity: 1 }]
        })
      });

      if (response.ok) {
        this.close();
        window.location.href = '/cart';
      }
    } catch (error) {
      console.error('Failed to add tractor to cart:', error);
    }
  }
}

customElements.define('package-builder', PackageBuilder);

/**
 * Showcase Card Slideshow
 * Simple image slideshow for product cards
 */
class ShowcaseSlideshow extends HTMLElement {
  constructor() {
    super();
    this.currentSlide = 0;
  }

  connectedCallback() {
    this.slides = this.querySelectorAll('[data-slide]');
    this.thumbnails = this.closest('.showcase-card')?.querySelectorAll('.showcase-card__thumbnail');
    this.slidesContainer = this.querySelector('[data-slides]');
    this.totalSlides = this.slides.length;

    if (this.totalSlides <= 1) return;

    const prevBtn = this.querySelector('[data-slide-prev]');
    const nextBtn = this.querySelector('[data-slide-next]');

    if (prevBtn) prevBtn.addEventListener('click', (e) => { e.preventDefault(); this.prev(); });
    if (nextBtn) nextBtn.addEventListener('click', (e) => { e.preventDefault(); this.next(); });

    if (this.thumbnails) {
      this.thumbnails.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.goTo(i));
      });
    }

    this.updateThumbnails();
  }

  prev() {
    this.goTo(this.currentSlide > 0 ? this.currentSlide - 1 : this.totalSlides - 1);
  }

  next() {
    this.goTo(this.currentSlide < this.totalSlides - 1 ? this.currentSlide + 1 : 0);
  }

  goTo(index) {
    this.currentSlide = index;
    if (this.slidesContainer) {
      this.slidesContainer.style.transform = `translateX(-${index * 100}%)`;
    }
    this.updateThumbnails();
  }

  updateThumbnails() {
    if (!this.thumbnails) return;
    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === this.currentSlide);
    });
  }
}

customElements.define('showcase-slideshow', ShowcaseSlideshow);
